/**
 * Group collection
 * @class
 */
import Base from '@netx/core/lib/netx-collection';
import Model from '../models/group';

import PagerModel from '@netx/core-app/models/pager';

export default class Groups extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {boolean} */
	get xhrTrackProgress() {
		return true;
	}

	/** @type {object} */
	get xhrLoadingMessages() {
		return {
			getGroupObjects: 'Loading groups',
			getGroups: 'Loading groups',
			getUsersForGroup: 'Loading users for group',
		};
	}

	/** @type {object} */
	get xhrLoadedMessages() {
		return {
			getGroupObjects: 'Loaded groups',
			getGroups: 'Loaded groups',
			getUsersForGroup: 'Loaded users for group',
		};
	}

	/**
	 * @property {object} methods            - The methods available on this model
	 * @property {method} methods.read       - The read method (get)
	 * @property {method} methods.getByIds   - Fetch groups by passed ids
	 * @property {method} methods.getGroups  - Fetch groups by `this.ids()`
	 */
	get methods() {
		return {
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			read(opts) {
				return [
					'getGroups',
					this.filterStr,
					{field: 'name', order: 'asc'},
					this.pager.getPageDTO(),
				];
			},
			/**
			 * @method
			 * @param {array} ids Group ids
			 * @param {object} opts Request options
			 */
			getByIds(ids, opts) {
				if (!_.isArray(ids)) ids = [ids];
				return ['getGroupObjects', ids];
			},
			// One day, the above method will be a pageable method - will still want access to a full fetch (like users)
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			getGroups(opts) {
				return this.getByIds(this.ids(), opts);
			},
		};
	}

	/**
	 * @property {object} parsers             - Collection parsers
	 * @property {function} parsers.getByIds  - Groups fetched by passed id
	 * @property {function} parsers.getGroups - Groups fetched
	 */
	get parsers() {
		return {
			/**
			 * @function
			 * @param {array} groups Groups
			 * @param {array} ids Group ids
			 * @param {object} opts Request options
			 */
			getByIds(groups, ids, opts = {}) {
				this.set(groups, opts);
				!opts.silent && this.trigger('sync', this, groups, opts);
			},
			/**
			 * @function
			 * @param {array} groups Groups
			 * @param {object} opts Request options
			 */
			getGroups(groups, opts = {}) {
				this.set(groups, opts);
				!opts.silent && this.trigger('sync', this, groups, opts);
			},
		};
	}

	/**
	 * @override
	 * @param {array} models An array of models or model attributes
	 * @param {object} opts Collection options
	 */
	constructor(models, opts = {}) {
		opts.model = opts.model || Model;

		super(models, opts);

		/**
		 * Paging model
		 * @memberof module:user/collections/groups
		 * @alias pager
		 * @type {module:app/models/pager}
		 */
		this.pager = new PagerModel(
			{
				// TODO: collections/models should not need to know about routes/routing
				routesToPage: false,
				pageSize: 25,
				...opts.pager,
			},
			{collection: this},
		);

		// Hey, listen
		this.listenTo(this, 'sync', (collection, data, opts) => {
			if (collection !== this) return;
			if (!opts.ignoreUsers) this.invoke('getUserIds');

			this.stopListening(this, 'add', this.addedGroup).listenTo(
				this,
				'add',
				this.addedGroup,
			);
		}).listenTo(
			this,
			['request:getGroups', 'request:getByIds'].join(' '),
			(collection, xhr, opts) => {
				this.trigger('request:read', this, xhr, opts);
			},
		);
	}

	/**
	 * Retrieve users for newly added group
	 * @method
	 * @param {Netx.Models.Group} group Group model
	 */
	addedGroup(group, groups, opts = {}) {
		if (opts.ignoreUsers === true) return;
		group.getUserIds();
	}

	/**
	 * @method
	 * @param {array} groups An array of group objects `{name: <name>}`
	 * @param {object} opts Options
	 */
	createNewGroups(groups, opts = {}) {
		return new Promise((resolve, reject) => {
			if (!groups) resolve();

			const existingPromises = [];

			const newGroups = [];
			// Save each group after the other has completed to avoid data locks on the server
			const next = () => {
				if (!groups.length) {
					Promise.all(existingPromises).then(() => {
						resolve(newGroups);
					}, reject);
					return;
				}

				const group = new Model(groups.shift());
				group.save(opts).then(
					() => {
						newGroups.push(group);
						next();
					},
					(err) => {
						// Duplicate name - just pass back and get users added
						if (err.code === -103) {
							const _group = new Model();
							existingPromises.push(
								new Promise((resolve, reject) => {
									_group.getByName(group.get('name')).then(
										() => {
											newGroups.push(_group);
											resolve();
										},
										(err) => {
											reject(err);
										},
									);
								}),
							);
							return next();
						}
						reject(err);
					},
				);
			};

			next();
		});
	}

	/**
	 * @method
	 * @param {array} ids Group ids
	 */
	deleteGroups(ids) {
		return this.deleteMultiple.apply(this, arguments);
	}

	/*
	 * @method
	 * @param {number} page Page number
	 * @param {object} opts Options
	 */
	gotoPage(page, opts) {
		if (page === this.curPage) {
			return new $.Deferred().resolveWith(this);
		}

		this.curPage = parseInt(page);
		// Getting a page for detail view
		return this.fetch(
			null,
			_.extend(
				{
					currentPage: page,
					duplicateQueryPolicy: 'abort',
				},
				opts,
			),
		);
	}

	/** @type {string} */
	static get __name__() {
		return 'Groups';
	}
}
